import React, { useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { ChargePoint, Connector } from '../common/redux/api/web-api'
import { ComboboxOption, ComboboxValue } from '../common/components/combobox'
import { isSupportDomain } from '../lib/currentDomain'
import { isConfigReadonly } from '../lib/is-config-readonly'
import isAura from '../lib/isAura'
import useText from '../common/hooks/useText'
import { EarthingSchemeLB } from '../common/enums/earthingSchemes'
import { Dropdown } from '../common/uikit/Dropdown'
import { Input } from '../common/uikit/Input'

import calculateMaxPower from './../lib/calculateMaxPower'
import Text from './Text'

export type OcppConnectorSettingsProps = {
    chargePoint: ChargePoint
    isLocked: boolean
    isOnline: boolean
    connector: Connector
    autoRefresh?: boolean
    phasesOptions?: ComboboxOption<ComboboxValue>[]
}

const OcppConnectorSettings = ({
    chargePoint,
    isOnline,
    isLocked = true,
    connector,
    phasesOptions,
}: OcppConnectorSettingsProps): null | React.ReactElement => {
    const text = useText()
    const {
        control,
        formState: { errors },
        register,
        trigger,
        setValue,
        watch,
    } = useFormContext()

    const earthingSystem = watch('earthingSystem')
    const installationPhases = watch('installationPhases')
    const supplyCurrent = watch('supplyCurrent')

    useEffect(() => {
        const subscription = watch(() => trigger())
        return () => subscription.unsubscribe()
    }, [trigger, watch])
    const calculateMaxPowerValue = calculateMaxPower(
        +connector?.factoryCurrent,
        installationPhases || 1,
        earthingSystem
    ).toFixed(2)

    const getInstallationPhaseDisabledReason = () => {
        if (isConfigReadonly('AssignedPhase', chargePoint?.ocppConfigKeys)) return text('msgOcppConfigKeyReadOnly')
        if (chargePoint.inActiveLoadBalancerGroup) return text('msgChargerInActiveLBGroup')
    }

    const checkPhasesInstalled = Number(installationPhases) === 3
    const showUserpreferredPhase =
        checkPhasesInstalled &&
        ((chargePoint?.acPhaseSwitchingSupported && earthingSystem === EarthingSchemeLB.IT) ||
            earthingSystem === EarthingSchemeLB.TN)

    useEffect(() => {
        if (!isAura(chargePoint)) {
            setValue(`ic_${connector.connectorId}`, supplyCurrent)
        }
    }, [chargePoint, connector.connectorId, setValue, supplyCurrent])
    return (
        <div>
            <div className="mt-1">
                {isAura(chargePoint) && (
                    <div className="mb-2" data-testid="charger-info">
                        <Text code="lblSocketInfo" values={{ name: connector.name }} />
                    </div>
                )}
                <div data-testid="charger-info-data" className="mb-2">
                    <Input
                        type="number"
                        label={text('lblMaxPower')}
                        data-testid={`max-power-value-${connector.connectorId}`}
                        value={calculateMaxPowerValue}
                        id={`maxPowerInput-${connector.connectorId}`}
                        disabled
                    />
                </div>
            </div>
            <div>
                <div className="mt-5 mb-2" data-testid="installationCurrent">
                    <Controller
                        name={`ic_${connector.connectorId}`}
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Input
                                label={text('lblInstallationCurrentLimitField')}
                                infoText={
                                    isAura(chargePoint)
                                        ? text('msgInstallationCurrentLimitField', {
                                              supplyCurrent: chargePoint.supplyCurrent,
                                              factoryCurrent: connector.factoryCurrent,
                                          })
                                        : text('msgOcppHaloInstallationCurrentLimitField')
                                }
                                type="number"
                                value={value}
                                id={`ic_${connector.connectorId}`}
                                disabled={
                                    isLocked ||
                                    !isOnline ||
                                    !isAura(chargePoint) ||
                                    (!isSupportDomain && chargePoint?.ocppEnabled)
                                }
                                data-testid={'installationCurrent-input-' + connector.connectorId}
                                error={errors[`ic_${connector.connectorId}`]?.message}
                                onChange={(e) => {
                                    onChange(e.target.value)
                                }}
                            />
                        )}
                    />
                </div>
                {showUserpreferredPhase && (
                    <div className="mt-5 mb-2" data-testid={`installationPhase_label_${connector.connectorId}`}>
                        <Controller
                            name={`installationPhase_${connector.connectorId}`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <Dropdown
                                    label={text('lblUserPreferredPhase')}
                                    infoText={text('msgUserPreferredPhase')}
                                    data-testid={`installationPhase_${connector.connectorId}`}
                                    id={`installationPhase_${connector.connectorId}`}
                                    options={phasesOptions}
                                    disabled={
                                        !isOnline ||
                                        chargePoint.inActiveLoadBalancerGroup ||
                                        isLocked ||
                                        chargePoint.ocppEnabled ||
                                        +installationPhases === 1 || //TODO: Needs to be fixed
                                        isConfigReadonly('AssignedPhase', chargePoint.ocppConfigKeys)
                                    }
                                    disabledReason={getInstallationPhaseDisabledReason()}
                                    value={value}
                                    onChange={(option) => {
                                        onChange(option.value)
                                    }}
                                />
                            )}
                        />
                    </div>
                )}
                <div className="mt-5 mb-2" data-testid="userCurrent">
                    <Input
                        type="number"
                        label={text('lblUserCurrentLimitField')}
                        infoText={text('msgUserCurrentLimitField')}
                        id={`uc_${connector.connectorId}`}
                        disabled={!isOnline || isLocked || (!isSupportDomain && chargePoint?.ocppEnabled)}
                        data-testid={'userCurrent-input-' + connector.connectorId}
                        error={errors[`uc_${connector.connectorId}`]?.message}
                        {...register(`uc_${connector.connectorId}`)}
                    />
                </div>
            </div>
        </div>
    )
}

export default OcppConnectorSettings
